import React from 'react';

import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import {
  DatePicker as MaterialDatepicker,
  DatePickerProps as MaterialDatepickerProps,
} from '@material-ui/pickers';

import {
  TextField,
  TextFieldProps,
} from '@waglabs/textfield';
import { Flex } from '@waglabs/flex-next';
import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box';

import { FieldHelperText } from '../FieldHelperText';

// https://next.material-ui-pickers.dev/guides/css-overrides
// https://github.com/mui-org/material-ui-pickers/issues/393#issuecomment-591747961
const wagTheme = createMuiTheme({
  palette: {
    primary: {
      main: theme.colors.wagGreen,
    },
  },
  overrides: {
    MuiPickersToolbar: {
      root: {
        backgroundColor: theme.colors.wagGreen,
        color: theme.colors.white,
      },
    },
    MuiPickersCalendarHeader: {
      root: {
        color: theme.colors.white,
      },
      monthTitleContainer: {
        color: theme.colors.medGray,
      },
    },
    MuiPickersDay: {
      root: {
        color: theme.colors.medGray,
        '&$selected': {
          backgroundColor: theme.colors.wagGreen,
          color: theme.colors.white,
        },
      },
      today: {
        color: theme.colors.medGray,
      },
    },
    MuiPickersYearSelection: {
      root: {
        color: theme.colors.medGray,
      },
    },
  },
});

/**
 * Known issues:
 *
 * 1. When you open the picker on mobile, there's a React prop error casing. Library issue
 * Since at the time of creating this we are using an alpha version.
 * It should be fixed on the next major release. (Functionality still works)
 *
 * 2. If you open the logs, you can see that React complains that the <Textfield />
 * doesn't have an onChange handler. However, this seems like
 * an issue with the library. (Functionality still works)
 */

/**
 * This is the first step on creating a reusable date picker
 * for Wag web ecosystem
 *
 * Note: Wait for the team to move this on NPM(maybe there's another datepicker idea)
 */

export type DatepickerProps = TextFieldProps & {
  pickerProps?: MaterialDatepickerProps;
  error?: boolean;
  helperText?: string;
  helperTextProps?: any;
};
export const Datepicker = ({
  onChange,
  value,
  pickerProps: {
    minDate,
    maxDate,
    ...restPickerProps
  } = {
    minDate: new Date('1970-01-01'),
    maxDate: new Date(),
  },
  helperText,
  helperTextProps,
  ...rest
}: DatepickerProps) => (
  <Box>
    <ThemeProvider theme={wagTheme}>
      <MaterialDatepicker
        clearable
        disableFuture
        {...restPickerProps}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
        value={value}
        renderInput={({
          inputRef: pickerInputRef,
          inputProps: pickerInputProps,
          InputProps: PickerInputProps,
        }) => {
          const EndAdornmentComponent = (
            PickerInputProps.endAdornment ? () => PickerInputProps.endAdornment : null
          );

          return (
            <TextField
              fullWidth
              {...pickerInputProps}
              {...rest}
              onBlur={(ev) => {
              // We need to call both onBlur
              // so the validation from Formik and effects for the datecpiker happens
                if (rest.onBlur) {
                  rest.onBlur(ev);
                }
                if (pickerInputProps.onBlur) {
                  pickerInputProps.onBlur(ev);
                }
              }}
              ref={pickerInputRef}
              inputProps={{
                endAdornment: () => EndAdornmentComponent && (
                <Flex
                  alignItems="center"
                >
                  <EndAdornmentComponent />
                </Flex>
                ),
                ...rest.inputProps,
              }}
            />
          );
        }}
      />
      {helperText && (
        <FieldHelperText
          text={helperText}
          error={rest.error}
          {...helperTextProps}
        />
      )}
    </ThemeProvider>
  </Box>
);
